"use client";

import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { reportClientError } from "@/lib/observability/report-client-error";
import { suppressErrorReporting } from "@/lib/observability/report-error";
import {
  fetchWithTransientRetry,
  TRANSIENT_RETRY_STATUS_SET,
} from "@/lib/utils/fetch-with-transient-retry";

interface CreatorResult {
  id: string;
  projectId: string;
  platform: string;
  platformLabel: string;
  title: string;
  publishedUrl: string | null;
  publishedAt: string | null;
  thumbnailUrl: string | null;
  creator: {
    userId: string;
    connectionId: string;
    platformUsername: string;
    imageUrl: string;
  };
  metrics: {
    views: number | null;
    meanViews: number | null;
    growthPercent: number | null;
  };
}

// Error thrown by loadCreatorResults when the final (post-retry) response is
// not ok. Captured `status` drives downstream classification — matches the
// FeaturedAccountsLoadError pattern in featured-creators.tsx so we don't rely
// on brittle message regex matching.
class CreatorResultsLoadError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`Failed to load creator results (HTTP ${status})`);
    this.name = "CreatorResultsLoadError";
    this.status = status;
  }
}

async function loadCreatorResults(): Promise<CreatorResult[]> {
  try {
    // SENTRY FIX: VIDSEEDS-2C6 — TrackedFetchError 503 GET /api/showcase/creator-results/
    // SENTRY FIX: VIDSEEDS-2C4 — Failed to load creator results
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: GKE ingress returns 503 during rolling-deploy windows; the
    // raw fetch surfaced each transient 5xx as an error-level event (1001
    // events on VIDSEEDS-2C6 alone). Baiduspider and real users hitting the
    // homepage during a rollout produced most of the volume.
    // Fix: retry transient 5xx/408/429 a few times before failing. On final
    // failure report as warning (infra layer, not an app bug).
    const response = await fetchWithTransientRetry(
      "/api/showcase/creator-results/",
      { cache: "no-store" },
    );

    if (!response.ok) {
      throw new CreatorResultsLoadError(response.status);
    }

    const payload = (await response.json()) as { results?: CreatorResult[] };
    return payload.results ?? [];
  } catch (error) {
    // Network drops on mobile devices fire TypeError: Failed to fetch.
    // The UI handles this gracefully (returns null), React Query retries,
    // and refetchOnReconnect restores data — downgrade to warning (NODE-220).
    const isNetworkError =
      error instanceof TypeError &&
      /Failed to fetch|Load failed/i.test(error.message);
    // SENTRY FIX: VIDSEEDS-2C4 — Failed to load creator results (post-retry).
    // Date: 2026-04-23 | Agent: Claude Code
    // Root cause: Same crawler + rolling-deploy pattern as VIDSEEDS-2RR (see
    //   featured-creators.tsx). Existing tracked-fetch classifier already
    //   suppresses the /api/showcase/* 503 at the fetch layer; the thrown
    //   "Failed to load creator results" after retry exhaustion was still
    //   producing a second warning-level issue. User impact is zero —
    //   CreatorResults returns null when isError is true.
    // Fix (v2 after Seer review): classify as post-retry-transient ONLY when
    //   the captured HTTP status was retryable. Non-retryable 4xx fall
    //   through to reportClientError so a genuine 400/401/403/404 on
    //   /api/showcase/creator-results keeps its regular error-level issue.
    const isPostRetryTransient =
      error instanceof CreatorResultsLoadError &&
      TRANSIENT_RETRY_STATUS_SET.has(error.status);
    if (isPostRetryTransient) {
      const transientError = error as CreatorResultsLoadError;
      suppressErrorReporting(transientError.message, {
        source: "components.CreatorResults",
        feature: "homepage",
        action: "loadCreatorResults",
        expected: true,
        extra: {
          postRetryTransient: true,
          status: transientError.status,
        },
      });
    } else {
      reportClientError(error, {
        source: "components.CreatorResults",
        feature: "homepage",
        action: "loadCreatorResults",
        ...(isNetworkError && { severity: "warning" }),
        extra:
          error instanceof CreatorResultsLoadError
            ? { status: error.status }
            : undefined,
      });
    }
    throw error;
  }
}

function formatMetric(value: number | null): string | null {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }

  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function formatPublishedDate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function CreatorResults() {
  const { t } = useJsonTranslation("HomepageCreatorResults");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["homepage-creator-results"],
    queryFn: loadCreatorResults,
    staleTime: 60 * 60 * 1000,
  });

  if (isError) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="relative px-5 py-6 sm:px-8 sm:py-8">
        <div className="space-y-8">
          <div className="space-y-3 text-center">
            <Skeleton className="bg-muted/70 mx-auto h-4 w-48 rounded-full" />
            <Skeleton className="bg-muted/70 mx-auto h-10 w-96 max-w-full rounded-full" />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={index}
                className="bg-card/70 h-[25rem] rounded-[32px]"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const results = data ?? [];
  if (results.length === 0) {
    return null;
  }

  return (
    <div
      data-home-block="creator-results"
      className="relative px-5 py-6 sm:px-8 sm:py-8"
    >
      <div>
        <div className="mx-auto mb-10 max-w-3xl text-center xl:mx-0 xl:text-start">
          <p className="text-primary text-xs font-semibold tracking-[0.35em] uppercase">
            {t("eyebrow")}
          </p>
          <h2 className="text-foreground font-home-display mt-4 text-3xl font-semibold tracking-normal text-balance sm:text-4xl">
            {t("title")}
          </h2>
          <p className="text-muted-foreground mt-4 text-base text-pretty sm:text-lg">
            {t("subtitle")}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {results.map((result) => {
            const views = formatMetric(result.metrics.views);
            const meanViews = formatMetric(result.metrics.meanViews);
            const growthPercent = result.metrics.growthPercent;
            const publishedDate = formatPublishedDate(result.publishedAt);

            return (
              <article
                key={result.id}
                className="uni-card overflow-hidden !p-0"
              >
                <div className="relative aspect-video overflow-hidden bg-[linear-gradient(135deg,rgba(14,165,233,0.2),rgba(249,115,22,0.18),rgba(16,185,129,0.18))]">
                  {result.thumbnailUrl ? (
                    <Image
                      src={result.thumbnailUrl}
                      alt={result.title}
                      fill
                      className="object-cover"
                      unoptimized
                    />
                  ) : null}
                  <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
                    <span className="border-border bg-surface-scrim/90 text-foreground rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
                      {result.platformLabel}
                    </span>
                    {views ? (
                      <span className="border-border bg-surface-scrim/90 text-foreground rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
                        {t("metrics.views", { value: views })}
                      </span>
                    ) : publishedDate ? (
                      <span className="border-border bg-surface-scrim/90 text-foreground rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
                        {t("publishedOn", { date: publishedDate })}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-5 p-[var(--space-card-padding)]">
                  <div className="flex items-center gap-3">
                    <div className="border-border relative h-11 w-11 overflow-hidden rounded-full border">
                      <Image
                        src={result.creator.imageUrl}
                        alt={result.creator.platformUsername}
                        fill
                        className="object-cover"
                        unoptimized
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-foreground truncate font-semibold">
                        {result.creator.platformUsername}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {result.platformLabel}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-foreground line-clamp-2 text-xl font-semibold">
                      {result.title}
                    </h3>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="border-border bg-surface-soft/80 rounded-2xl border p-2 text-center sm:p-3">
                      <p className="text-muted-foreground mb-1 truncate text-[9px] tracking-[0.15em] uppercase sm:text-[10px] sm:tracking-[0.2em]">
                        {t("statLabels.meanViews")}
                      </p>
                      <p className="text-foreground text-base font-semibold sm:text-lg">
                        {meanViews ?? "—"}
                      </p>
                    </div>
                    <div className="border-border bg-surface-soft/80 rounded-2xl border p-2 text-center sm:p-3">
                      <p className="text-muted-foreground mb-1 truncate text-[9px] tracking-[0.15em] uppercase sm:text-[10px] sm:tracking-[0.2em]">
                        {t("statLabels.thisVideo")}
                      </p>
                      <p className="text-foreground text-base font-semibold sm:text-lg">
                        {views ?? "—"}
                      </p>
                    </div>
                    <div className="border-border bg-surface-soft/80 rounded-2xl border p-2 text-center sm:p-3">
                      <p className="text-muted-foreground mb-1 truncate text-[9px] tracking-[0.15em] uppercase sm:text-[10px] sm:tracking-[0.2em]">
                        {t("statLabels.growth")}
                      </p>
                      <p className="text-primary flex items-center justify-center gap-1 text-lg font-semibold">
                        {growthPercent != null ? (
                          <>
                            <TrendingUp
                              className="h-4 w-4"
                              aria-hidden="true"
                            />
                            +{growthPercent}%
                          </>
                        ) : (
                          "—"
                        )}
                      </p>
                    </div>
                  </div>

                  {result.publishedUrl ? (
                    <Button asChild variant="outline" className="w-full">
                      <Link
                        href={result.publishedUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("cta")}
                        <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </Button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
